/**
 * The simulation. Pure: no DOM, no clock, no canvas. `step(world, view, dt, rng)` advances it,
 * `launch(world, azimuth, elevation, power)` throws the thing in hand (`setItem` picks it): it
 * leaves the rail and flies a real parabola; what it lands next to is what it hits. The view is only used to know
 * how wide the visible sea is when spawning. Events pile up in `world.events` for the view to
 * drain each frame (sounds, haptics, toasts).
 */
import { halfWidthAt, type View } from './project.ts';
import type { Rng } from './rng.ts';

const DEG = Math.PI / 180;

export type Mode = 'practice' | 'basic' | 'sloth' | 'og' | 'feed';

export interface ModeDef {
  label: string;
  blurb: string;
  /** seconds on the clock; Infinity for no clock */
  duration: number;
  /** a flat count for every throwable, or null for each thing's own count */
  ammo: number | null;
  /** or a count per thing (wins over `ammo`) */
  ammoByKind?: Partial<Record<ThrowKind, number>>;
  /** how much more often sharks come */
  sharkRate: number;
  /** may turtles, pelicans and snorkelers come for the boat? */
  chargers: boolean;
  /** how often a monkey on a log comes to raid the bucket (0 = never, 1 = OG's rate) */
  monkeyRate: number;
  /** the leaderboard this mode posts to; null means never posted */
  game: string | null;
  /** the bonus round: the family floats by, mouths open now and then; no coconuts, no creatures */
  feed?: boolean;
}

export const MODES: Record<Mode, ModeDef> = {
  practice: { label: 'Practice', blurb: 'No clock, unlimited bucket, nothing posted.', duration: Infinity, ammo: 99, sharkRate: 1, chargers: false, monkeyRate: 0, game: null },
  basic: { label: 'Basic', blurb: 'Unlimited bucket, the usual sea.', duration: 90, ammo: 99, sharkRate: 1, chargers: false, monkeyRate: 0, game: 'cocoshot-basic' },
  sloth: { label: 'Bro', blurb: '25 rocks, 10 pineapples, 10 fish, 5 shoes. A monkey may come for them.', duration: 90, ammo: null, sharkRate: 1, chargers: false, monkeyRate: 0.5, game: 'cocoshot' },
  og: {
    label: 'OG',
    blurb: 'Sharks twice as often, turtles, pelicans and snorkelers may come for you, and the murder monkeys paddle out for your fish. A fish stops a turtle or a pelican, a shoe stops a snorkeler, a pineapple buys off a monkey.',
    duration: 90,
    ammo: null,
    sharkRate: 2,
    chargers: true,
    monkeyRate: 1,
    game: 'cocoshot-og',
  },
  feed: {
    label: 'Feed Me',
    blurb: 'Bonus round. Zach, Annabelle and Ed float by one at a time with their mouths open now and then. Throw things in. Every mouth fed earns something for next time; the fifth earns a super pineapple.',
    duration: 60,
    ammo: null,
    ammoByKind: { rock: 15, pineapple: 6, fish: 8, shoe: 4 },
    sharkRate: 0,
    chargers: false,
    monkeyRate: 0,
    game: 'cocoshot-feed',
    feed: true,
  },
};
export const MODE_KEYS: Mode[] = ['practice', 'basic', 'sloth', 'og', 'feed'];

export type ThrowKind = 'rock' | 'pineapple' | 'fish' | 'shoe' | 'super';

/** what a thrown thing does when it lands next to a creature: scare (a strike for a friend), feed (it likes it), repel (it leaves, and you're glad), bribe (it takes it and leaves) */
export type Reaction = 'scare' | 'feed' | 'repel' | 'bribe';

export interface Throwable {
  label: string;
  emoji: string;
  /** launch speed at full power, m/s. Speed goes with sqrt(power) so range goes with power. */
  vMax: number;
  /** a hit: the thing lands within this of a coconut (m), growing with distance */
  hitRadius: (d: number) => number;
  /** points multiplier for a coconut hit */
  pointsMul: number;
  /** seconds before the next throw */
  reload: number;
  /** how many can be in the air at once */
  maxInFlight: number;
  /** how many you get per round */
  count: number;
  /** what happens to each creature it lands next to (or, for the pelican, hits in the air) */
  on: { turtle: Reaction; snorkeler: Reaction; shark: Reaction; pelican: Reaction; monkey: Reaction; grad: Reaction };
  /** per creature: how near is near enough (m at distance d); unset means it has to land on it */
  lure?: Partial<Record<Target, (d: number) => number>>;
  /** earned, never free: steers itself to the nearest target and cannot miss */
  homing?: boolean;
}

/**
 * The things in the bucket. Everything cracks a coconut; what else it does depends on the pairing.
 * The pairings are the game's joke, so add to `on` rather than special-casing in `land()`.
 */
export const THROWABLES: Record<ThrowKind, Throwable> = {
  rock: { label: 'rock', emoji: '🪨', vMax: 20, hitRadius: (d) => 0.4 + 0.045 * d, pointsMul: 1, reload: 0.35, maxInFlight: 3, count: 25, on: { turtle: 'scare', snorkeler: 'scare', shark: 'repel', pelican: 'scare', monkey: 'repel', grad: 'scare' } },
  // heavy and round: shorter, but a big splash zone and more points
  pineapple: {
    label: 'pineapple',
    emoji: '🍍',
    vMax: 17,
    hitRadius: (d) => 0.5 + 0.055 * d,
    pointsMul: 1.3,
    reload: 0.5,
    maxInFlight: 2,
    count: 10,
    on: { turtle: 'scare', snorkeler: 'scare', shark: 'repel', pelican: 'scare', monkey: 'bribe', grad: 'scare' },
    // a monkey will paddle a few metres for a pineapple
    lure: { monkey: (d) => 3 + 0.12 * d },
  },
  // slippery: tighter zone, pays best; a turtle is glad of it and a shark forgets about the boat
  fish: {
    label: 'fish',
    emoji: '🐟',
    vMax: 21,
    hitRadius: (d) => 0.32 + 0.04 * d,
    pointsMul: 1.5,
    reload: 0.4,
    maxInFlight: 3,
    count: 10,
    // a monkey catches a thrown fish and keeps coming: the trap
    on: { turtle: 'feed', snorkeler: 'scare', shark: 'feed', pelican: 'feed', monkey: 'feed', grad: 'scare' },
    // insurance: a shark smells a fish from a long way off, and a hungry turtle or pelican goes for one nearby
    lure: { shark: (d) => 5 + 0.2 * d, turtle: (d) => 3.5 + 0.15 * d, pelican: (d) => 3.5 + 0.15 * d, monkey: (d) => 3 + 0.12 * d },
  },
  // only five, but it lands flat and makes the biggest splash: the easiest hit in the bucket; a snorkeler gets the message
  shoe: {
    label: 'shoe',
    emoji: '👟',
    vMax: 18,
    hitRadius: (d) => 0.65 + 0.07 * d,
    pointsMul: 1.2,
    reload: 0.4,
    maxInFlight: 3,
    count: 5,
    on: { turtle: 'scare', snorkeler: 'repel', shark: 'repel', pelican: 'scare', monkey: 'repel', grad: 'repel' },
    lure: { snorkeler: (d) => 3 + 0.15 * d, grad: (d) => 3 + 0.15 * d },
  },
  // the super pineapple: earned in the Feed round (one per five mouths), homes on the nearest target
  super: {
    label: 'super pineapple',
    emoji: '🍍',
    vMax: 20,
    hitRadius: (d) => 0.6 + 0.06 * d,
    pointsMul: 2,
    reload: 0.5,
    maxInFlight: 1,
    count: 0,
    on: { turtle: 'scare', snorkeler: 'scare', shark: 'repel', pelican: 'scare', monkey: 'repel', grad: 'scare' },
    homing: true,
  },
};
/** what one super pineapple costs in other things from the bag (above what a Feed Me round hands out) */
export const SUPER_PRICES: Partial<Record<ThrowKind, number>> = { rock: 8, fish: 4, shoe: 4, pineapple: 3 };

/** buy one super pineapple with `kind` from the bag; null if the bag can't afford it */
export function tradeForSuper(bag: Specials, kind: ThrowKind): Specials | null {
  const price = SUPER_PRICES[kind];
  if (!price || (bag[kind] ?? 0) < price) return null;
  return { ...bag, [kind]: bag[kind] - price, super: (bag.super ?? 0) + 1 };
}

/** what each mouth fed in a round earns for next time, cycling; the fifth is the super pineapple */
export const REWARDS: ThrowKind[] = ['rock', 'fish', 'pineapple', 'shoe', 'super'];
export const EARN_EVERY = REWARDS.length;

export const THROW_KINDS: ThrowKind[] = ['rock', 'pineapple', 'fish', 'shoe', 'super'];
/** the things in the bucket every round; `super` only shows when you have some */
export const BUCKET_KINDS: ThrowKind[] = ['rock', 'pineapple', 'fish', 'shoe'];

/** what a throw can land on */
export type Target = 'coconut' | 'turtle' | 'snorkeler' | 'shark' | 'pelican' | 'mouth' | 'monkey' | 'grad' | 'cap';
export const TARGET_EMOJI: Record<Target, string> = { coconut: '🥥', turtle: '🐢', snorkeler: '🤿', shark: '🦈', pelican: '🦤', mouth: '😮', monkey: '🐒', grad: '🎓', cap: '🧢' };
/** a tally key: what was thrown at what */
export type Pairing = `${ThrowKind}:${Target}`;

export const PHYSICS = {
  g: 9.81,
  /** the throw leaves the rail this far above the water (m) */
  launchH: 1.9,
  /** degrees left/right the gun can swing */
  azimuthMax: 55,
  elevationMin: 3,
  elevationMax: 80,
  /** a strike: within this of a surfaced turtle (m) */
  turtleRadius: (d: number) => 0.75 + 0.05 * d,
  snorkelerRadius: (d: number) => 0.8 + 0.05 * d,
  /** a shark is big */
  sharkRadius: (d: number) => 1.1 + 0.05 * d,
  /** a pelican is hit in the air: within this of its body, in three dimensions (m) */
  pelicanRadius: (d: number) => 0.9 + 0.04 * d,
  /** the monkey's log is a fair-sized target */
  monkeyRadius: (d: number) => 1.2 + 0.05 * d,
  /** a Whittie on a pool float */
  gradRadius: (d: number) => 0.8 + 0.05 * d,
  /** the tossed cap, in the air */
  capRadius: (d: number) => 0.45 + 0.03 * d,
};

export const TUNING = {
  duration: 90,
  /** points for a turtle taking a fish */
  feedPoints: 60,
  /** seconds added to the clock per coconut */
  timePerCoconut: 5,
  /** a snorkeler sent off with a shoe */
  shoePoints: 50,
  /** a Whitman grad on a pool float, tossing their cap */
  grad: {
    dMin: 8,
    dMax: 26,
    every: [22, 38] as const,
    firstAt: 12,
    rise: 0.8,
    up: [10, 16] as const,
    dive: 0.7,
    tossEvery: [2.5, 4.5] as const,
    /** the cap leaves the hand this fast (m/s) from this height (m) */
    tossV: 5.5,
    handH: 1.5,
    shoePoints: 45,
    capPoints: 60,
  },
  /** the murder monkeys: a white-faced capuchin on a driftwood log, paddling for your fish */
  monkey: {
    dMin: 24,
    dMax: 34,
    every: [26, 44] as const,
    firstAt: 18,
    speed: 1.5,
    /** it is aboard inside this (m) */
    reachD: 3,
    /** what it takes: fish first, then pineapples, then a shoe to throw overboard */
    steals: { fish: 3, pineapple: 2, shoe: 1 } as const,
    repelPoints: 60,
    bribePoints: 25,
  },
  /** the Feed round */
  feed: {
    // near enough that a flat throw arrives in about a second
    dMin: 9,
    dMax: 13,
    /** a head stays this long, then the next comes */
    stay: 14,
    gap: 1.2,
    rise: 0.9,
    /** closed for a while, then opens to a random width, holds long enough for a throw to arrive, closes */
    closedFor: [1.0, 2.2] as const,
    openFor: [1.4, 2.4] as const,
    openTime: 0.3,
    minOpen: 0.45,
    /** points per thing that goes in */
    points: { rock: 30, pineapple: 80, fish: 50, shoe: 40, super: 150 } as const,
  },
  /** OG: what it costs when a creature reaches the boat, and how fast each comes */
  boarding: {
    turtle: { cost: 80, speed: 2.2, chance: 0.4 },
    pelican: { cost: 80, speed: 6, chance: 0.5 },
    snorkeler: { cost: 100, speed: 1.8, chance: 0.4 },
  },
  pelican: {
    dMin: 12,
    dMax: 40,
    /** flight height above the water (m) */
    hMin: 2.5,
    hMax: 7,
    speed: [4, 6.5] as const,
    every: [14, 26] as const,
    firstAt: 7,
    feedPoints: 70,
  },
  snorkeler: {
    dMin: 6,
    dMax: 24,
    every: [16, 30] as const,
    firstAt: 10,
    rise: 0.6,
    up: [7, 13] as const,
    dive: 0.6,
  },
  shark: {
    dMin: 14,
    dMax: 40,
    every: [22, 40] as const,
    firstAt: 14,
    speed: [6, 9] as const,
    /** chance a crossing shark turns and comes for the boat */
    chargeChance: 0.45,
    chargeSpeed: 5,
    /** it has reached the hull inside this (m) */
    chompD: 3,
    chompCost: 150,
    repelPoints: 80,
    feedPoints: 120,
    /** blood in the water: each bite adds this to the charge chance and shortens the wait for the next shark */
    bloodChance: 0.2,
    bloodHaste: 0.3,
    /** what the shark takes, bite by bite; after the last one the round is over */
    limbs: ['a leg', 'the other leg', 'an arm', 'the other arm'] as const,
  },
  boat: {
    /** scenery only: always beyond the longest shot */
    dMin: 95,
    dMax: 190,
    every: [18, 40] as const,
    firstAt: 4,
  },
  coconut: {
    radius: 0.13, // m
    dMin: 5,
    dMax: 50,
    max: 9,
    spawnEvery: [1.7, 0.75] as const, // start → end of round (s)
    sinkTime: 0.8,
  },
  turtle: {
    radius: 0.38, // m, shell half-length
    dMin: 7,
    dMax: 40,
    every: [9, 15] as const, // s between surfacings
    firstAt: 6,
    rise: 0.8,
    up: [3, 5.5] as const,
    dive: 0.7,
  },
  strikesToEnd: 3,
  /** misses in a row before Annabelle has something to say */
  heckleEvery: 3,
  /** streak lengths Zach cheers */
  cheerStreaks: [3, 5, 8, 12, 16, 20],
  /** a hit from at least this far gets a cheer (m) */
  cheerFar: 25,
  strikeCost: 100,
  streakStep: 0.1,
  streakMax: 0.5,
};

export interface Coconut {
  id: number;
  x: number;
  d: number;
  vx: number;
  vd: number;
  phase: number;
  phase2: number;
  /** radians, how the coconut sits in the water */
  tilt: number;
  /** slow rotation of that tilt */
  spin: number;
  state: 'afloat' | 'hit';
  hitAt: number;
}

export interface Turtle {
  id: number;
  x: number;
  d: number;
  vx: number;
  /** OG: when it turns for the boat (Infinity = never), and whether it has */
  turnAt: number;
  charging: boolean;
  born: number;
  /** seconds at the surface before diving on its own */
  up: number;
  /** set when startled: dives from that moment */
  scaredAt: number;
  gone: boolean;
}

/** a snorkeler at the surface: a friend, near, slow, and very easy to hit by accident */
export interface Snorkeler {
  id: number;
  x: number;
  d: number;
  vx: number;
  turnAt: number;
  charging: boolean;
  born: number;
  up: number;
  scaredAt: number;
  gone: boolean;
}

/** a pelican flying across above the water; it catches a fish thrown its way */
export interface Pelican {
  id: number;
  x: number;
  d: number;
  h: number;
  vx: number;
  turnAt: number;
  charging: boolean;
  born: number;
  /** flapping phase */
  phase: number;
  state: 'fly' | 'gone';
  goneAt: number;
}

/** a Whitman graduate on a pool float: a friend, who tosses their cap now and then */
export interface Grad {
  id: number;
  x: number;
  d: number;
  vx: number;
  born: number;
  up: number;
  scaredAt: number;
  gone: boolean;
  nextTossAt: number;
  /** the cap in the air, if it is */
  cap: { h: number; vh: number; t0: number } | null;
}

/** a capuchin on a log, paddling straight for the boat to rob the bucket */
export interface Monkey {
  id: number;
  x: number;
  d: number;
  state: 'coming' | 'gone';
  born: number;
  goneAt: number;
  /** it caught a thrown fish on the way: it paddles a little faster, pleased */
  fed: number;
}

/** a shark: a fin racing across, which sometimes turns and comes straight at the boat */
export interface Shark {
  id: number;
  x: number;
  d: number;
  vx: number;
  vd: number;
  state: 'cross' | 'charge' | 'gone';
  born: number;
  /** when, while crossing, it decides whether to turn */
  turnAt: number;
  decided: boolean;
  /** when it left (dived or chomped), for the fade */
  goneAt: number;
}

export type Who = 'zach' | 'annabelle' | 'ed';

/** how each face is framed: where the mouth is in its picture (fractions of the square) and how far the jaw drops */
export interface FaceFrame {
  /** metres the head picture is across */
  metres: number;
  /** fraction of the picture height that sits at the waterline */
  waterline: number;
  mouthX: number;
  mouthY: number;
  /** fraction of the picture height the jaw drops when fully open */
  jawDrop: number;
}
export const FACES: Record<Who, FaceFrame> = {
  // squares of 3400, 3240 and 2000 photo px; the mouth centre is the jaw polygon's top, in fractions of the square
  // mouthY is the middle of the open mouth: the lip line plus half the jaw drop (lip lines at 0.715, 0.738, 0.70)
  zach: { metres: 4.4, waterline: 0.95, mouthX: 0.49, mouthY: 0.77, jawDrop: 0.12 },
  annabelle: { metres: 4.4, waterline: 0.95, mouthX: 0.5, mouthY: 0.79, jawDrop: 0.12 },
  ed: { metres: 4.4, waterline: 0.95, mouthX: 0.525, mouthY: 0.765, jawDrop: 0.11 },
};

/** a head floating by in the Feed round */
export interface Face {
  who: Who;
  x: number;
  d: number;
  vx: number;
  /** depth drift: the head swims nearer and farther over its stay */
  dMid: number;
  dSwing: number;
  dPhase: number;
  born: number;
  /** 0 closed .. 1 fully open, right now */
  open: number;
  /** what the mouth is doing and when it changes */
  phase: 'closed' | 'opening' | 'open' | 'closing';
  phaseAt: number;
  /** how far this opening goes */
  wide: number;
  state: 'up' | 'gone';
  goneAt: number;
}

/** a thrown thing in flight: metres and metres per second, `h` above the water */
export interface Projectile {
  kind: ThrowKind;
  x: number;
  d: number;
  h: number;
  vx: number;
  vd: number;
  vh: number;
  firedAt: number;
  /** a homing throw: what it is steering for (a coconut id, or the face), and when it will arrive */
  target?: { kind: 'coconut'; id: number } | { kind: 'face' };
  arriveAt?: number;
}

/** a far catamaran crossing the bay: scenery, never within reach */
export interface Boat {
  id: number;
  x: number;
  d: number;
  vx: number;
  /** +1 sails to the right, -1 to the left (the sprite is mirrored) */
  heading: number;
  scale: number;
}

export interface Splash {
  x: number;
  d: number;
  t0: number;
  big: boolean;
  /** seconds it stays visible: a miss lingers so the player can correct */
  life: number;
}

export interface Pop {
  text: string;
  x: number;
  d: number;
  t0: number;
  good: boolean;
}

export type GameEvent =
  | { type: 'fire'; kind: ThrowKind }
  | { type: 'hit'; pts: number; d: number; streak: number }
  | { type: 'miss'; d: number; close: boolean; kind: ThrowKind }
  /** a friend liked what landed next to it */
  | { type: 'feed'; what: 'turtle' | 'pelican'; pts: number; kind: ThrowKind }
  | { type: 'strike'; what: 'turtle' | 'snorkeler' | 'pelican' | 'grad'; strikes: number }
  /** a shoe reached a Whittie: Go Blues */
  | { type: 'goBlues'; pts: number }
  /** something knocked the tossed cap out of the air */
  | { type: 'capped'; pts: number }
  /** a shark has turned toward the boat */
  | { type: 'charge' }
  /** the shark reached the hull */
  | { type: 'chomp'; cost: number; bites: number; limb: string }
  /** something landed on the shark and it left: fed if it was a fish */
  | { type: 'repel'; pts: number; kind: ThrowKind; fed: boolean }
  /** a snorkeler got a shoe and left, pleased or not */
  | { type: 'shoed'; pts: number }
  /** a monkey is paddling out */
  | { type: 'monkey' }
  /** the monkey got aboard and took things from the bucket */
  | { type: 'robbed'; took: ThrowKind; n: number }
  /** the monkey left: tipped off its log, or bought off with a pineapple, or it caught a fish and kept coming */
  | { type: 'monkeyOff'; how: 'tipped' | 'bribed' | 'clapped'; pts: number; kind: ThrowKind | null }
  | { type: 'monkeyFed'; kind: ThrowKind }
  /** OG: a creature has turned for the boat */
  | { type: 'incoming'; what: 'turtle' | 'pelican' | 'snorkeler' }
  /** OG: a creature reached the boat */
  | { type: 'boarded'; what: 'turtle' | 'pelican' | 'snorkeler'; cost: number }
  /** a coconut bought more time */
  | { type: 'bonus'; seconds: number }
  /** Feed: something went in */
  | { type: 'fed'; who: Who; kind: ThrowKind; pts: number }
  /** Feed: something hit a closed face */
  | { type: 'bonk'; who: Who; kind: ThrowKind }
  /** Feed: a new head is up */
  | { type: 'face'; who: Who }
  /** something earned for next time (total fed this round) */
  | { type: 'earned'; what: ThrowKind; fed: number }
  /** misses in a row have reached a multiple of TUNING.heckleEvery */
  | { type: 'heckle'; misses: number }
  /** something worth a cheer: a streak milestone or a long hit */
  | { type: 'cheer'; why: 'streak' | 'far' | 'first'; n: number }
  | { type: 'item'; kind: ThrowKind }
  /** the thing in hand ran out (and the hand moved on to `next`, or nothing is left) */
  | { type: 'empty'; kind: ThrowKind; next: ThrowKind | null }
  | { type: 'over'; reason: 'time' | 'strikes' | 'eaten' };

export interface World {
  mode: Mode;
  t: number;
  /** seconds on the clock (Infinity in practice); grows with coconuts */
  duration: number;
  score: number;
  streak: number;
  strikes: number;
  /** times the shark reached the hull */
  bites: number;
  shots: number;
  hits: number;
  /** misses in a row right now */
  missStreak: number;
  farthest: number;
  /** metres, summed over coconut hits */
  distance: number;
  coconuts: Coconut[];
  turtles: Turtle[];
  snorkelers: Snorkeler[];
  sharks: Shark[];
  pelicans: Pelican[];
  monkeys: Monkey[];
  nextMonkeyAt: number;
  grads: Grad[];
  nextGradAt: number;
  /** Feed: the head floating by, if any */
  face: Face | null;
  nextFaceAt: number;
  faceOrder: Who[];
  boats: Boat[];
  projectiles: Projectile[];
  /** the thing in hand */
  item: ThrowKind;
  /** how many of each are left this round */
  ammo: Record<ThrowKind, number>;
  /** Feed: mouths fed this round, for earning */
  fed: number;
  /** things earned this round, for next time */
  earned: Partial<Record<ThrowKind, number>>;
  /** the bucket as it was before extras and the super were added (to work out what is left over) */
  base: Record<ThrowKind, number>;
  /** landings on something, by what was thrown at what */
  tally: Partial<Record<Pairing, number>>;
  splashes: Splash[];
  pops: Pop[];
  nextCoconutAt: number;
  nextTurtleAt: number;
  nextSnorkelerAt: number;
  nextSharkAt: number;
  nextPelicanAt: number;
  nextBoatAt: number;
  reloadUntil: number;
  /** +1 or -1: which way the current carries things */
  current: number;
  over: boolean;
  events: GameEvent[];
  nextId: number;
}

/** what the player brings to a round beyond the bucket: earned extras of each thing, and super pineapples */
export type Specials = Record<ThrowKind, number>;
export const NO_SPECIALS: Specials = { rock: 0, pineapple: 0, fish: 0, shoe: 0, super: 0 };

export function createWorld(rng: Rng, mode: Mode = 'sloth', specials: Specials = NO_SPECIALS): World {
  const m = MODES[mode];
  const count = (k: Exclude<ThrowKind, 'super'>): number => m.ammoByKind?.[k] ?? m.ammo ?? THROWABLES[k].count;
  // practice brings nothing in and earns nothing
  const extra = (k: ThrowKind): number => (m.game ? Math.max(0, specials[k] ?? 0) : 0);
  const base: Record<ThrowKind, number> = { rock: count('rock'), pineapple: count('pineapple'), fish: count('fish'), shoe: count('shoe'), super: 0 };
  return {
    mode,
    t: 0,
    duration: m.duration,
    score: 0,
    streak: 0,
    strikes: 0,
    bites: 0,
    shots: 0,
    hits: 0,
    missStreak: 0,
    farthest: 0,
    distance: 0,
    coconuts: [],
    turtles: [],
    snorkelers: [],
    sharks: [],
    pelicans: [],
    monkeys: [],
    nextMonkeyAt: TUNING.monkey.firstAt,
    grads: [],
    nextGradAt: TUNING.grad.firstAt,
    face: null,
    nextFaceAt: 1.0,
    faceOrder: [],
    boats: [],
    projectiles: [],
    item: 'rock',
    ammo: { rock: base.rock + extra('rock'), pineapple: base.pineapple + extra('pineapple'), fish: base.fish + extra('fish'), shoe: base.shoe + extra('shoe'), super: extra('super') },
    base,
    fed: 0,
    earned: {},
    tally: {},
    splashes: [],
    pops: [],
    nextCoconutAt: 0.4,
    nextTurtleAt: TUNING.turtle.firstAt,
    nextSnorkelerAt: TUNING.snorkeler.firstAt,
    nextSharkAt: TUNING.shark.firstAt,
    nextPelicanAt: TUNING.pelican.firstAt,
    nextBoatAt: TUNING.boat.firstAt,
    reloadUntil: 0,
    current: rng.sign(),
    over: false,
    events: [],
    nextId: 1,
  };
}

/** 0 at the start of the round, 1 after the standard 90 s: the difficulty ramp, whatever the clock says */
export function progress(w: World): number {
  return Math.min(1, w.t / 90);
}

/** height of the water surface under a floating thing, metres above mean */
export function bobHeight(t: number, phase: number, phase2: number): number {
  const T = 2 * Math.PI;
  return 0.1 * Math.sin((T * t) / 2.3 + phase) + 0.22 * Math.sin((T * t) / 7.1 + phase2);
}

/** how far out of the water a surfacing creature is: 0 under, 1 fully up */
function surfaced(tu: { born: number; up: number; scaredAt: number }, t: number, rise: number, dive: number): number {
  const age = t - tu.born;
  if (age < 0) return 0;
  const upFrom = tu.born + rise;
  const diveAt = tu.scaredAt > 0 ? Math.min(tu.scaredAt, upFrom + tu.up) : upFrom + tu.up;
  if (t < upFrom) return Math.min(1, age / rise) * (t < diveAt ? 1 : 0);
  if (t < diveAt) return 1;
  return Math.max(0, 1 - (t - diveAt) / dive);
}

export function turtleUp(tu: Turtle, t: number): number {
  if (tu.charging && tu.scaredAt === 0) return 1;
  return surfaced(tu, t, TUNING.turtle.rise, TUNING.turtle.dive);
}

export function gradUp(g: Grad, t: number): number {
  return surfaced(g, t, TUNING.grad.rise, TUNING.grad.dive);
}

export function snorkelerUp(sn: Snorkeler, t: number): number {
  if (sn.charging && sn.scaredAt === 0) return 1;
  return surfaced(sn, t, TUNING.snorkeler.rise, TUNING.snorkeler.dive);
}

/** points for a coconut at distance d: linear plus a square term, because a far hit means a long wait */
export function pointsFor(d: number, streak: number): number {
  const base = Math.round((10 + 2 * d + 0.04 * d * d) / 5) * 5;
  const bonus = Math.min(TUNING.streakMax, streak * TUNING.streakStep);
  return Math.round(base * (1 + bonus));
}

function spawnCoconut(w: World, v: View, rng: Rng): void {
  const c = TUNING.coconut;
  // bias toward the middle distances early, outward as the round goes on
  const p = progress(w);
  const u = rng.next() ** (1.6 - 0.9 * p);
  const d = c.dMin + u * (c.dMax - c.dMin);
  const hw = halfWidthAt(v, d);
  // enter from the up-current edge most of the time, or appear mid-screen (as if it had been hidden by a swell)
  const fromEdge = rng.next() < 0.7;
  const x = fromEdge ? -w.current * (hw + 0.5) : rng.range(-hw * 0.8, hw * 0.8);
  const speed = rng.range(0.25, 0.7) * (0.8 + 0.6 * p);
  w.coconuts.push({
    id: w.nextId++,
    x,
    d,
    vx: w.current * speed,
    vd: rng.range(-0.12, 0.12),
    phase: rng.range(0, 6.28),
    phase2: rng.range(0, 6.28),
    tilt: rng.range(0, 6.28),
    spin: rng.range(-0.4, 0.4),
    state: 'afloat',
    hitAt: 0,
  });
}

function spawnTurtle(w: World, v: View, rng: Rng): void {
  const tu = TUNING.turtle;
  const d = rng.range(tu.dMin, tu.dMax);
  const hw = halfWidthAt(v, d);
  const up = rng.range(tu.up[0], tu.up[1]);
  const turns = MODES[w.mode].chargers && rng.next() < TUNING.boarding.turtle.chance;
  w.turtles.push({
    id: w.nextId++,
    x: rng.range(-hw * 0.75, hw * 0.75),
    d,
    vx: w.current * rng.range(0.05, 0.2),
    turnAt: turns ? w.t + tu.rise + rng.range(0.5, up * 0.6) : Infinity,
    charging: false,
    born: w.t,
    up,
    scaredAt: 0,
    gone: false,
  });
}

function tally(w: World, kind: ThrowKind, target: Target): void {
  const key: Pairing = `${kind}:${target}`;
  w.tally[key] = (w.tally[key] ?? 0) + 1;
}

/** something has landed at (x, d): decide what, if anything, it got */
function spawnSnorkeler(w: World, v: View, rng: Rng): void {
  const sn = TUNING.snorkeler;
  const d = rng.range(sn.dMin, sn.dMax);
  const hw = halfWidthAt(v, d);
  const up = rng.range(sn.up[0], sn.up[1]);
  const turns = MODES[w.mode].chargers && rng.next() < TUNING.boarding.snorkeler.chance;
  w.snorkelers.push({ id: w.nextId++, x: rng.range(-hw * 0.7, hw * 0.7), d, vx: w.current * rng.range(0.1, 0.3), turnAt: turns ? w.t + sn.rise + rng.range(0.5, up * 0.5) : Infinity, charging: false, born: w.t, up, scaredAt: 0, gone: false });
}

function spawnShark(w: World, v: View, rng: Rng): void {
  const sh = TUNING.shark;
  const d = rng.range(sh.dMin, sh.dMax);
  const hw = halfWidthAt(v, d);
  const heading = rng.sign();
  const speed = rng.range(sh.speed[0], sh.speed[1]);
  // it decides about the boat somewhere in the middle third of its crossing
  const crossing = (2 * hw) / speed;
  w.sharks.push({ id: w.nextId++, x: -heading * (hw + 2), d, vx: heading * speed, vd: 0, state: 'cross', born: w.t, turnAt: w.t + crossing * rng.range(0.3, 0.65), decided: false, goneAt: 0 });
}

function spawnPelican(w: World, v: View, rng: Rng): void {
  const pe = TUNING.pelican;
  const d = rng.range(pe.dMin, pe.dMax);
  const hw = halfWidthAt(v, d);
  const heading = rng.sign();
  const speed = rng.range(pe.speed[0], pe.speed[1]);
  const turns = MODES[w.mode].chargers && rng.next() < TUNING.boarding.pelican.chance;
  w.pelicans.push({ id: w.nextId++, x: -heading * (hw + 2), d, h: rng.range(pe.hMin, pe.hMax), vx: heading * speed, turnAt: turns ? w.t + ((2 * hw) / speed) * rng.range(0.25, 0.6) : Infinity, charging: false, born: w.t, phase: rng.range(0, 6.28), state: 'fly', goneAt: 0 });
}

/** a thrown thing has met a pelican in the air */
function hitPelican(w: World, pe: Pelican, kind: ThrowKind): void {
  tally(w, kind, 'pelican');
  pe.state = 'gone';
  pe.goneAt = w.t;
  if (THROWABLES[kind].on.pelican === 'feed') {
    const pts = TUNING.pelican.feedPoints;
    w.score += pts;
    w.missStreak = 0;
    w.pops.push({ text: `+${pts} 🦤`, x: pe.x, d: pe.d, t0: w.t, good: true });
    w.events.push({ type: 'feed', what: 'pelican', pts, kind });
    return;
  }
  w.strikes += 1;
  w.streak = 0;
  w.missStreak = 0;
  w.score = Math.max(0, w.score - TUNING.strikeCost);
  w.pops.push({ text: `-${TUNING.strikeCost}`, x: pe.x, d: pe.d, t0: w.t, good: false });
  w.events.push({ type: 'strike', what: 'pelican', strikes: w.strikes });
  if (w.strikes >= TUNING.strikesToEnd) endRound(w, 'strikes');
}

function spawnGrad(w: World, v: View, rng: Rng): void {
  const g = TUNING.grad;
  const d = rng.range(g.dMin, g.dMax);
  const hw = halfWidthAt(v, d);
  w.grads.push({ id: w.nextId++, x: rng.range(-hw * 0.7, hw * 0.7), d, vx: w.current * rng.range(0.1, 0.3), born: w.t, up: rng.range(g.up[0], g.up[1]), scaredAt: 0, gone: false, nextTossAt: w.t + g.rise + rng.range(1, 2.5), cap: null });
}

function stepGrads(w: World, dt: number, rng: Rng): void {
  const g = TUNING.grad;
  for (let i = w.grads.length - 1; i >= 0; i--) {
    const gr = w.grads[i];
    gr.x += gr.vx * dt;
    if (gradUp(gr, w.t) >= 1 && gr.scaredAt === 0 && !gr.cap && w.t >= gr.nextTossAt) {
      gr.cap = { h: g.handH, vh: g.tossV, t0: w.t };
    }
    if (gr.cap) {
      gr.cap.vh -= PHYSICS.g * dt;
      gr.cap.h += gr.cap.vh * dt;
      if (gr.cap.vh < 0 && gr.cap.h <= g.handH) {
        gr.cap = null; // caught
        gr.nextTossAt = w.t + rng.range(g.tossEvery[0], g.tossEvery[1]);
      }
    }
    if (w.t - gr.born > g.rise && gradUp(gr, w.t) <= 0) gr.gone = true;
    if (gr.gone) w.grads.splice(i, 1);
  }
}

function spawnMonkey(w: World, v: View, rng: Rng): void {
  const m = TUNING.monkey;
  const d = rng.range(m.dMin, m.dMax);
  const hw = halfWidthAt(v, d);
  w.monkeys.push({ id: w.nextId++, x: rng.range(-hw * 0.7, hw * 0.7), d, state: 'coming', born: w.t, goneAt: 0, fed: 0 });
  w.events.push({ type: 'monkey' });
}

/** the player clapped twice: any monkey still paddling turns around (what the guide said to do) */
export function clapOff(w: World): boolean {
  let any = false;
  for (const mk of w.monkeys) {
    if (mk.state !== 'coming') continue;
    mk.state = 'gone';
    mk.goneAt = w.t;
    w.score += TUNING.monkey.repelPoints;
    w.missStreak = 0;
    w.pops.push({ text: `+${TUNING.monkey.repelPoints} 👏`, x: mk.x, d: mk.d, t0: w.t, good: true });
    w.events.push({ type: 'monkeyOff', how: 'clapped', pts: TUNING.monkey.repelPoints, kind: null });
    any = true;
  }
  return any;
}

/** the monkey is aboard: it takes from the bucket, fish first */
function rob(w: World, mk: Monkey): void {
  const wants: Array<[ThrowKind, number]> = [['fish', TUNING.monkey.steals.fish], ['pineapple', TUNING.monkey.steals.pineapple], ['shoe', TUNING.monkey.steals.shoe]];
  let took: ThrowKind = 'shoe';
  let n = 0;
  for (const [kind, want] of wants) {
    if (w.ammo[kind] > 0) {
      n = Math.min(want, w.ammo[kind]);
      w.ammo[kind] -= n;
      took = kind;
      break;
    }
  }
  mk.state = 'gone';
  mk.goneAt = w.t;
  w.streak = 0;
  w.pops.push({ text: n ? `-${n} ${THROWABLES[took].emoji}` : 'nothing to steal', x: 0, d: 4, t0: w.t, good: false });
  w.events.push({ type: 'robbed', took, n });
}

function stepMonkeys(w: World, v: View, dt: number): void {
  for (let i = w.monkeys.length - 1; i >= 0; i--) {
    const mk = w.monkeys[i];
    if (mk.state === 'gone') {
      if (w.t - mk.goneAt > 1.2) w.monkeys.splice(i, 1);
      continue;
    }
    const n = Math.hypot(mk.x, mk.d) || 1;
    const sp = TUNING.monkey.speed * (1 + 0.25 * mk.fed);
    mk.x += (-mk.x / n) * sp * dt;
    mk.d += (-mk.d / n) * sp * dt;
    if (mk.d <= TUNING.monkey.reachD) rob(w, mk);
  }
  void v;
}

function spawnFace(w: World, v: View, rng: Rng): void {
  if (w.faceOrder.length === 0) {
    const all: Who[] = ['zach', 'annabelle', 'ed'];
    while (all.length) w.faceOrder.push(all.splice(Math.floor(rng.next() * all.length), 1)[0]);
  }
  const who = w.faceOrder.shift()!;
  const f = TUNING.feed;
  const d = rng.range(f.dMin, f.dMax);
  const hw = halfWidthAt(v, d);
  w.face = { who, x: rng.range(-hw * 0.4, hw * 0.4), d, vx: rng.sign() * rng.range(0.4, 0.9), dMid: d, dSwing: rng.range(2, 4), dPhase: rng.range(0, 6.28), born: w.t, open: 0, phase: 'closed', phaseAt: w.t + f.rise + rng.range(f.closedFor[0], f.closedFor[1]), wide: 1, state: 'up', goneAt: 0 };
  w.events.push({ type: 'face', who });
}

/** something went into the mouth */
function feedFace(w: World, face: Face, kind: ThrowKind): void {
  const pts = TUNING.feed.points[kind];
  w.score += pts;
  w.hits += 1;
  w.streak += 1;
  w.missStreak = 0;
  w.fed += 1;
  tally(w, kind, 'mouth');
  const m = mouthPoint(face);
  w.pops.push({ text: `+${pts} ${THROWABLES[kind].emoji}`, x: m.x, d: m.d, t0: w.t, good: true });
  w.events.push({ type: 'fed', who: face.who, kind, pts });
  if (MODES[w.mode].game) {
    const what = REWARDS[(w.fed - 1) % REWARDS.length];
    w.earned[what] = (w.earned[what] ?? 0) + 1;
    w.events.push({ type: 'earned', what, fed: w.fed });
  }
}

/** what goes back in the bag after a round: unthrown extras (base ammo is thrown first) plus this round's earnings */
export function leftovers(w: World, brought: Specials): Specials {
  const out = { ...NO_SPECIALS };
  for (const k of THROW_KINDS) {
    const unused = Math.max(0, Math.min(brought[k] ?? 0, w.ammo[k]));
    out[k] = unused + (w.earned[k] ?? 0);
  }
  return out;
}

/** the mouth's world position: a point above the water in front of the face */
export function mouthPoint(face: Face): { x: number; d: number; h: number } {
  const fr = FACES[face.who];
  return { x: face.x + (fr.mouthX - 0.5) * fr.metres, d: face.d, h: (fr.waterline - fr.mouthY) * fr.metres };
}

function stepFace(w: World, v: View, dt: number, rng: Rng): void {
  const f = TUNING.feed;
  const face = w.face;
  if (!face) {
    if (w.t >= w.nextFaceAt) spawnFace(w, v, rng);
    return;
  }
  if (face.state === 'gone') {
    if (w.t - face.goneAt > 0.8) {
      w.face = null;
      w.nextFaceAt = w.t + f.gap;
    }
    return;
  }
  // drift, bouncing inside the middle of the view, and nearer and farther on a slow swing
  face.x += face.vx * dt;
  face.d = Math.max(6, face.dMid + face.dSwing * Math.sin((w.t - face.born) * 0.45 + face.dPhase));
  const hw = halfWidthAt(v, face.d) * 0.55;
  if (face.x > hw) face.vx = -Math.abs(face.vx);
  if (face.x < -hw) face.vx = Math.abs(face.vx);
  // the mouth
  if (w.t >= face.phaseAt) {
    if (face.phase === 'closed') {
      face.phase = 'opening';
      face.wide = rng.range(f.minOpen, 1);
      face.phaseAt = w.t + f.openTime;
    } else if (face.phase === 'opening') {
      face.phase = 'open';
      face.phaseAt = w.t + rng.range(f.openFor[0], f.openFor[1]);
    } else if (face.phase === 'open') {
      face.phase = 'closing';
      face.phaseAt = w.t + f.openTime;
    } else {
      face.phase = 'closed';
      face.phaseAt = w.t + rng.range(f.closedFor[0], f.closedFor[1]);
    }
  }
  const target = face.phase === 'open' ? face.wide : face.phase === 'closed' ? 0 : face.phase === 'opening' ? face.wide : 0;
  face.open += (target - face.open) * Math.min(1, (dt / f.openTime) * 2.2);
  if (w.t - face.born > f.stay) {
    face.state = 'gone';
    face.goneAt = w.t;
  }
}

function spawnBoat(w: World, v: View, rng: Rng): void {
  const b = TUNING.boat;
  const d = rng.range(b.dMin, b.dMax);
  const hw = halfWidthAt(v, d);
  const heading = rng.sign();
  w.boats.push({ id: w.nextId++, x: -heading * (hw + 12), d, vx: heading * rng.range(1.5, 3), heading, scale: rng.range(0.8, 1.2) });
}

function land(w: World, x: number, d: number, kind: ThrowKind): void {
  if (d < 1.5) {
    // dropped it at the hull
    w.streak = 0;
    w.splashes.push({ x, d: 1.5, t0: w.t, big: false, life: 1.2 });
    w.events.push({ type: 'miss', d: 1.5, close: false, kind });
    missed(w);
    return;
  }
  let nearest: Coconut | null = null;
  let nearestDist = Infinity;
  for (const c of w.coconuts) {
    if (c.state !== 'afloat') continue;
    const dist = Math.hypot(c.x - x, c.d - d);
    if (dist < nearestDist) {
      nearestDist = dist;
      nearest = c;
    }
  }
  const thing = THROWABLES[kind];
  const hitR = thing.hitRadius(d);
  if (nearest && nearestDist <= hitR) {
    const c = nearest;
    const pts = Math.round(pointsFor(c.d, w.streak) * thing.pointsMul);
    tally(w, kind, 'coconut');
    w.streak += 1;
    w.missStreak = 0;
    w.score += pts;
    w.hits += 1;
    w.farthest = Math.max(w.farthest, c.d);
    w.distance += c.d;
    w.distance += c.d;
    c.state = 'hit';
    c.hitAt = w.t;
    w.splashes.push({ x: c.x, d: c.d, t0: w.t, big: true, life: 1.1 });
    w.pops.push({ text: `+${pts}`, x: c.x, d: c.d, t0: w.t, good: true });
    w.events.push({ type: 'hit', pts, d: c.d, streak: w.streak });
    if (Number.isFinite(w.duration)) {
      w.duration += TUNING.timePerCoconut;
      w.events.push({ type: 'bonus', seconds: TUNING.timePerCoconut });
    }
    if (w.hits === 1) w.events.push({ type: 'cheer', why: 'first', n: 1 });
    else if (TUNING.cheerStreaks.includes(w.streak)) w.events.push({ type: 'cheer', why: 'streak', n: w.streak });
    else if (c.d >= TUNING.cheerFar) w.events.push({ type: 'cheer', why: 'far', n: Math.round(c.d) });
    return;
  }
  // a shark: anything landing on it sends it away, and you want that; a fish only has to land near it
  for (const sh of w.sharks) {
    if (sh.state === 'gone') continue;
    const reach = Math.max(thing.lure?.shark?.(d) ?? 0, PHYSICS.sharkRadius(d));
    if (Math.hypot(sh.x - x, sh.d - d) > reach) continue;
    const fed = thing.on.shark === 'feed';
    const pts = fed ? TUNING.shark.feedPoints : TUNING.shark.repelPoints;
    tally(w, kind, 'shark');
    sh.state = 'gone';
    sh.goneAt = w.t;
    w.score += pts;
    w.missStreak = 0;
    w.splashes.push({ x, d, t0: w.t, big: true, life: 1.1 });
    w.pops.push({ text: `+${pts} 🦈`, x: sh.x, d: sh.d, t0: w.t, good: true });
    w.events.push({ type: 'repel', pts, kind, fed });
    return;
  }
  // a monkey on its log: tip it with anything, buy it off with a pineapple, or feed it a fish by mistake
  for (const mk of w.monkeys) {
    if (mk.state !== 'coming') continue;
    const reach = Math.max(thing.lure?.monkey?.(d) ?? 0, PHYSICS.monkeyRadius(d));
    if (Math.hypot(mk.x - x, mk.d - d) > reach) continue;
    tally(w, kind, 'monkey');
    const how = thing.on.monkey;
    if (how === 'feed') {
      mk.fed += 1;
      w.pops.push({ text: 'it caught the fish', x: mk.x, d: mk.d, t0: w.t, good: false });
      w.events.push({ type: 'monkeyFed', kind });
      return;
    }
    const pts = how === 'bribe' ? TUNING.monkey.bribePoints : TUNING.monkey.repelPoints;
    mk.state = 'gone';
    mk.goneAt = w.t;
    w.score += pts;
    w.missStreak = 0;
    w.splashes.push({ x, d, t0: w.t, big: true, life: 1.1 });
    w.pops.push({ text: `+${pts} 🐒`, x: mk.x, d: mk.d, t0: w.t, good: true });
    w.events.push({ type: 'monkeyOff', how: how === 'bribe' ? 'bribed' : 'tipped', pts, kind });
    return;
  }
  // a Whittie on a float: a shoe is a tradition, anything else is a strike
  for (const gr of w.grads) {
    if (gr.gone || gradUp(gr, w.t) <= 0.2) continue;
    const reach = Math.max(thing.lure?.grad?.(d) ?? 0, PHYSICS.gradRadius(d));
    if (Math.hypot(gr.x - x, gr.d - d) > reach) continue;
    tally(w, kind, 'grad');
    gr.scaredAt = w.t;
    if (thing.on.grad === 'repel') {
      const pts = TUNING.grad.shoePoints;
      w.score += pts;
      w.missStreak = 0;
      w.splashes.push({ x, d, t0: w.t, big: true, life: 1.1 });
      w.pops.push({ text: `+${pts} 🎓`, x: gr.x, d: gr.d, t0: w.t, good: true });
      w.events.push({ type: 'goBlues', pts });
      return;
    }
    w.strikes += 1;
    w.streak = 0;
    w.missStreak = 0;
    w.score = Math.max(0, w.score - TUNING.strikeCost);
    w.splashes.push({ x, d, t0: w.t, big: true, life: 1.1 });
    w.pops.push({ text: `-${TUNING.strikeCost}`, x: gr.x, d: gr.d, t0: w.t, good: false });
    w.events.push({ type: 'strike', what: 'grad', strikes: w.strikes });
    if (w.strikes >= TUNING.strikesToEnd) endRound(w, 'strikes');
    return;
  }
  // a snorkeler: nothing you throw is welcome, except that a shoe gets the message across
  for (const sn of w.snorkelers) {
    if (sn.gone || snorkelerUp(sn, w.t) <= 0.2) continue;
    const reach = Math.max(thing.lure?.snorkeler?.(d) ?? 0, PHYSICS.snorkelerRadius(d));
    if (Math.hypot(sn.x - x, sn.d - d) > reach) continue;
    tally(w, kind, 'snorkeler');
    sn.scaredAt = w.t;
    if (thing.on.snorkeler === 'repel') {
      const pts = TUNING.shoePoints;
      w.score += pts;
      w.missStreak = 0;
      w.splashes.push({ x, d, t0: w.t, big: true, life: 1.1 });
      w.pops.push({ text: `+${pts} 👟`, x: sn.x, d: sn.d, t0: w.t, good: true });
      w.events.push({ type: 'shoed', pts });
      return;
    }
    w.strikes += 1;
    w.streak = 0;
    w.missStreak = 0;
    w.score = Math.max(0, w.score - TUNING.strikeCost);
    w.splashes.push({ x, d, t0: w.t, big: true, life: 1.1 });
    w.pops.push({ text: `-${TUNING.strikeCost}`, x: sn.x, d: sn.d, t0: w.t, good: false });
    w.events.push({ type: 'strike', what: 'snorkeler', strikes: w.strikes });
    if (w.strikes >= TUNING.strikesToEnd) endRound(w, 'strikes');
    return;
  }
  for (const tu of w.turtles) {
    if (tu.gone || turtleUp(tu, w.t) <= 0.2) continue;
    const reach = Math.max(thing.lure?.turtle?.(d) ?? 0, PHYSICS.turtleRadius(d));
    if (Math.hypot(tu.x - x, tu.d - d) > reach) continue;
    tu.scaredAt = w.t; // it dives either way: startled, or off with its fish
    tally(w, kind, 'turtle');
    if (thing.on.turtle === 'feed') {
      const pts = TUNING.feedPoints;
      w.score += pts;
      w.missStreak = 0;
      w.splashes.push({ x, d, t0: w.t, big: false, life: 1.1 });
      w.pops.push({ text: `+${pts} 🐟`, x: tu.x, d: tu.d, t0: w.t, good: true });
      w.events.push({ type: 'feed', what: 'turtle', pts, kind });
      return;
    }
    w.strikes += 1;
    w.streak = 0;
    w.missStreak = 0;
    w.score = Math.max(0, w.score - TUNING.strikeCost);
    w.splashes.push({ x, d, t0: w.t, big: true, life: 1.1 });
    w.pops.push({ text: `-${TUNING.strikeCost}`, x: tu.x, d: tu.d, t0: w.t, good: false });
    w.events.push({ type: 'strike', what: 'turtle', strikes: w.strikes });
    if (w.strikes >= TUNING.strikesToEnd) endRound(w, 'strikes');
    return;
  }
  w.streak = 0;
  const close = nearest !== null && nearestDist <= 2.2 * hitR;
  w.splashes.push({ x, d, t0: w.t, big: false, life: 2.6 });
  if (close) w.pops.push({ text: 'close!', x, d, t0: w.t, good: false });
  w.events.push({ type: 'miss', d, close, kind });
  missed(w);
}

function missed(w: World): void {
  w.missStreak += 1;
  if (w.missStreak % TUNING.heckleEvery === 0) w.events.push({ type: 'heckle', misses: w.missStreak });
}

/** OG: a turtle, pelican or snorkeler reached the boat */
function board(w: World, what: 'turtle' | 'pelican' | 'snorkeler'): void {
  const cost = TUNING.boarding[what].cost;
  w.score = Math.max(0, w.score - cost);
  w.streak = 0;
  w.splashes.push({ x: 0, d: 3, t0: w.t, big: true, life: 1.2 });
  w.pops.push({ text: `-${cost}`, x: 0, d: 4, t0: w.t, good: false });
  w.events.push({ type: 'boarded', what, cost });
}

/** the chance a crossing shark turns on the boat, higher with blood in the water */
export function chargeChance(w: World): number {
  return Math.min(0.95, TUNING.shark.chargeChance + TUNING.shark.bloodChance * w.bites);
}

function endRound(w: World, reason: 'time' | 'strikes' | 'eaten'): void {
  if (w.over) return;
  w.over = true;
  w.events.push({ type: 'over', reason });
}

/** advance the world by dt seconds */
export function step(w: World, v: View, dt: number, rng: Rng): void {
  if (w.over) return;
  w.t += dt;
  const p = progress(w);

  if (MODES[w.mode].feed) {
    stepFace(w, v, dt, rng);
  }
  // spawning (not in the Feed round)
  if (!MODES[w.mode].feed && w.t >= w.nextCoconutAt) {
    const afloat = w.coconuts.reduce((n, c) => n + (c.state === 'afloat' ? 1 : 0), 0);
    if (afloat < TUNING.coconut.max) spawnCoconut(w, v, rng);
    const [a, b] = TUNING.coconut.spawnEvery;
    w.nextCoconutAt = w.t + (a + (b - a) * p) * rng.range(0.7, 1.3);
  }
  if (!MODES[w.mode].feed && w.t >= w.nextTurtleAt) {
    spawnTurtle(w, v, rng);
    const [a, b] = TUNING.turtle.every;
    w.nextTurtleAt = w.t + rng.range(a, b) * (1 - 0.35 * p);
  }
  if (w.t >= w.nextBoatAt) {
    spawnBoat(w, v, rng);
    const [a, b] = TUNING.boat.every;
    w.nextBoatAt = w.t + rng.range(a, b);
  }
  if (!MODES[w.mode].feed && w.t >= w.nextSnorkelerAt) {
    spawnSnorkeler(w, v, rng);
    const [a, b] = TUNING.snorkeler.every;
    w.nextSnorkelerAt = w.t + rng.range(a, b);
  }
  if (MODES[w.mode].sharkRate > 0 && w.t >= w.nextSharkAt) {
    spawnShark(w, v, rng);
    const [a, b] = TUNING.shark.every;
    w.nextSharkAt = w.t + Math.max(6, (rng.range(a, b) / MODES[w.mode].sharkRate) * (1 - 0.3 * p) * (1 - TUNING.shark.bloodHaste * Math.min(2, w.bites)));
  }
  if (!MODES[w.mode].feed && w.t >= w.nextPelicanAt) {
    spawnPelican(w, v, rng);
    const [a, b] = TUNING.pelican.every;
    w.nextPelicanAt = w.t + rng.range(a, b);
  }
  if (MODES[w.mode].monkeyRate > 0 && w.t >= w.nextMonkeyAt && !w.monkeys.some((m) => m.state === 'coming')) {
    spawnMonkey(w, v, rng);
    const [a, b] = TUNING.monkey.every;
    w.nextMonkeyAt = w.t + rng.range(a, b) / MODES[w.mode].monkeyRate;
  }
  stepMonkeys(w, v, dt);
  if (!MODES[w.mode].feed && w.t >= w.nextGradAt) {
    spawnGrad(w, v, rng);
    const [a, b] = TUNING.grad.every;
    w.nextGradAt = w.t + rng.range(a, b);
  }
  stepGrads(w, dt, rng);
  for (let i = w.pelicans.length - 1; i >= 0; i--) {
    const pe = w.pelicans[i];
    if (pe.state === 'gone') {
      // flies off and up
      pe.x += pe.vx * 1.6 * dt;
      pe.h += 2.5 * dt;
      if (w.t - pe.goneAt > 1.5) w.pelicans.splice(i, 1);
      continue;
    }
    if (!pe.charging && w.t >= pe.turnAt) {
      pe.charging = true;
      w.events.push({ type: 'incoming', what: 'pelican' });
    }
    if (pe.charging) {
      // straight at the rail, coming down to it
      const n = Math.hypot(pe.x, pe.d) || 1;
      const sp = TUNING.boarding.pelican.speed;
      pe.x += (-pe.x / n) * sp * dt;
      pe.d += (-pe.d / n) * sp * dt;
      pe.vx = (-pe.x / n) * sp;
      pe.h += (2.2 - pe.h) * Math.min(1, 1.2 * dt);
      if (pe.d <= TUNING.shark.chompD) {
        board(w, 'pelican');
        w.pelicans.splice(i, 1);
        continue;
      }
    } else {
      pe.x += pe.vx * dt;
    }
    if (Math.abs(pe.x) > halfWidthAt(v, pe.d) + 3) w.pelicans.splice(i, 1);
  }
  for (let i = w.snorkelers.length - 1; i >= 0; i--) {
    const sn = w.snorkelers[i];
    if (!sn.charging && sn.scaredAt === 0 && w.t >= sn.turnAt) {
      sn.charging = true;
      w.events.push({ type: 'incoming', what: 'snorkeler' });
    }
    if (sn.charging && sn.scaredAt === 0) {
      const n = Math.hypot(sn.x, sn.d) || 1;
      sn.x += (-sn.x / n) * TUNING.boarding.snorkeler.speed * dt;
      sn.d += (-sn.d / n) * TUNING.boarding.snorkeler.speed * dt;
      if (sn.d <= TUNING.shark.chompD) {
        board(w, 'snorkeler');
        w.snorkelers.splice(i, 1);
        continue;
      }
    } else {
      sn.x += sn.vx * dt;
    }
    if (w.t - sn.born > TUNING.snorkeler.rise && snorkelerUp(sn, w.t) <= 0) sn.gone = true;
    if (sn.gone) w.snorkelers.splice(i, 1);
  }
  for (let i = w.sharks.length - 1; i >= 0; i--) {
    const sh = w.sharks[i];
    if (sh.state === 'gone') {
      if (w.t - sh.goneAt > 0.8) w.sharks.splice(i, 1);
      continue;
    }
    if (sh.state === 'cross') {
      sh.x += sh.vx * dt;
      if (!sh.decided && w.t >= sh.turnAt) {
        sh.decided = true;
        if (rng.next() < chargeChance(w)) {
          sh.state = 'charge';
          w.events.push({ type: 'charge' });
        }
      }
      if (Math.abs(sh.x) > halfWidthAt(v, sh.d) + 3) w.sharks.splice(i, 1);
    } else {
      // straight at the boat
      const n = Math.hypot(sh.x, sh.d) || 1;
      sh.vx = (-sh.x / n) * TUNING.shark.chargeSpeed;
      sh.vd = (-sh.d / n) * TUNING.shark.chargeSpeed;
      sh.x += sh.vx * dt;
      sh.d += sh.vd * dt;
      if (sh.d <= TUNING.shark.chompD) {
        sh.state = 'gone';
        sh.goneAt = w.t;
        w.score = Math.max(0, w.score - TUNING.shark.chompCost);
        w.streak = 0;
        w.bites += 1;
        const limb = TUNING.shark.limbs[Math.min(w.bites, TUNING.shark.limbs.length) - 1];
        w.splashes.push({ x: sh.x, d: Math.max(2, sh.d), t0: w.t, big: true, life: 1.2 });
        w.pops.push({ text: `-${TUNING.shark.chompCost}`, x: sh.x, d: Math.max(3, sh.d), t0: w.t, good: false });
        w.events.push({ type: 'chomp', cost: TUNING.shark.chompCost, bites: w.bites, limb });
        if (w.bites >= TUNING.shark.limbs.length) endRound(w, 'eaten');
      }
    }
  }
  for (let i = w.boats.length - 1; i >= 0; i--) {
    const b = w.boats[i];
    b.x += b.vx * dt;
    if (Math.abs(b.x) > halfWidthAt(v, b.d) + 14) w.boats.splice(i, 1);
  }

  // drift
  const sink = TUNING.coconut.sinkTime;
  for (let i = w.coconuts.length - 1; i >= 0; i--) {
    const c = w.coconuts[i];
    if (c.state === 'hit') {
      if (w.t - c.hitAt > sink) w.coconuts.splice(i, 1);
      continue;
    }
    c.x += c.vx * dt;
    c.d += c.vd * dt;
    c.tilt += c.spin * dt;
    const hw = halfWidthAt(v, c.d);
    if (Math.abs(c.x) > hw + 1.2 || c.d < 3 || c.d > TUNING.coconut.dMax + 10) w.coconuts.splice(i, 1);
  }
  for (let i = w.turtles.length - 1; i >= 0; i--) {
    const tu = w.turtles[i];
    if (!tu.charging && tu.scaredAt === 0 && w.t >= tu.turnAt) {
      tu.charging = true;
      w.events.push({ type: 'incoming', what: 'turtle' });
    }
    if (tu.charging && tu.scaredAt === 0) {
      const n = Math.hypot(tu.x, tu.d) || 1;
      tu.x += (-tu.x / n) * TUNING.boarding.turtle.speed * dt;
      tu.d += (-tu.d / n) * TUNING.boarding.turtle.speed * dt;
      if (tu.d <= TUNING.shark.chompD) {
        board(w, 'turtle');
        w.turtles.splice(i, 1);
        continue;
      }
    } else {
      tu.x += tu.vx * dt;
    }
    const age = w.t - tu.born;
    if (age > TUNING.turtle.rise && turtleUp(tu, w.t) <= 0) tu.gone = true;
    if (tu.gone) w.turtles.splice(i, 1);
  }

  // things in flight: a parabola each, landed when it reaches the water
  for (let i = w.projectiles.length - 1; i >= 0; i--) {
    const h = w.projectiles[i];
    if (h.target && h.arriveAt !== undefined) {
      // a homing throw: steer straight for where the target is now, arriving on time, with a hump in the air
      const T = h.arriveAt - h.firedAt;
      const remaining = h.arriveAt - w.t;
      let tx = 0, td = 0, th = 0, alive = false;
      if (h.target.kind === 'coconut') {
        const c = w.coconuts.find((k) => k.id === (h.target as { id: number }).id && k.state === 'afloat');
        if (c) { tx = c.x; td = c.d; th = 0; alive = true; }
      } else if (w.face && w.face.state === 'up') {
        const m = mouthPoint(w.face);
        tx = m.x; td = m.d; th = m.h; alive = true;
      }
      if (alive && remaining > 0.03) {
        const k = Math.min(1, dt / remaining);
        h.x += (tx - h.x) * k;
        h.d += (td - h.d) * k;
        const u = 1 - remaining / T;
        h.h = th + (PHYSICS.launchH - th) * (1 - u) + 3 * Math.sin(Math.PI * u);
        continue;
      }
      if (alive) {
        // arrival: it cannot miss
        if (h.target.kind === 'face' && w.face) {
          w.face.open = 1;
          w.face.phase = 'open';
          w.face.phaseAt = w.t + 0.6;
          feedFace(w, w.face, h.kind);
        } else {
          land(w, tx, td, h.kind);
        }
        w.projectiles.splice(i, 1);
        continue;
      }
      // the target is gone: fall the rest of the way like anything else
      h.target = undefined;
    }
    h.vh -= PHYSICS.g * dt;
    h.x += h.vx * dt;
    h.d += h.vd * dt;
    h.h += h.vh * dt;
    // a face in the way? Where the throw crosses the face's plane decides: inside the open mouth's
    // rectangle it is fed, on the face it is a bonk, close by says so, and beside the head it sails on.
    let caught = false;
    const face = w.face;
    if (face && face.state === 'up') {
      const prevD = h.d - h.vd * dt;
      const crossing = (prevD < face.d && h.d >= face.d) || (prevD > face.d && h.d <= face.d) || Math.abs(h.d - face.d) < 0.12;
      if (crossing) {
        // where it was when it crossed the plane
        const u = h.vd !== 0 ? Math.max(0, Math.min(1, (face.d - prevD) / (h.d - prevD || 1e-9))) : 1;
        const cx = h.x - h.vx * dt * (1 - u);
        const ch = h.h - h.vh * dt * (1 - u);
        const fr = FACES[face.who];
        const m = mouthPoint(face);
        const halfW = 0.19 * fr.metres; // the cavity is about 38% of the picture wide
        const halfH = 0.2 + 0.5 * fr.jawDrop * fr.metres * face.open; // as open as the jaw is, plus a little
        const dx = Math.abs(cx - m.x);
        const dh = Math.abs(ch - m.h);
        if (face.open > 0.15 && dx <= halfW && dh <= halfH) {
          feedFace(w, face, h.kind);
          caught = true;
        } else {
          const headHalfW = 0.42 * fr.metres;
          const headTop = fr.waterline * fr.metres;
          if (Math.abs(cx - face.x) <= headHalfW && ch >= -0.2 && ch <= headTop) {
            w.streak = 0;
            w.pops.push({ text: dx <= halfW * 1.8 && dh <= halfH + 0.6 ? 'close!' : 'bonk', x: face.x, d: face.d, t0: w.t, good: false });
            w.events.push({ type: 'bonk', who: face.who, kind: h.kind });
            missed(w);
            caught = true;
          }
        }
      }
    }
    // a tossed cap in the way?
    if (!caught) {
      for (const gr of w.grads) {
        if (!gr.cap) continue;
        if (Math.hypot(gr.x - h.x, gr.d - h.d, gr.cap.h - h.h) > PHYSICS.capRadius(gr.d)) continue;
        const pts = TUNING.grad.capPoints;
        w.score += pts;
        w.missStreak = 0;
        tally(w, h.kind, 'cap');
        w.pops.push({ text: `+${pts} 🧢`, x: gr.x, d: gr.d, t0: w.t, good: true });
        w.events.push({ type: 'capped', pts });
        gr.cap = null;
        gr.nextTossAt = w.t + 4;
        caught = true;
        break;
      }
    }
    for (const pe of w.pelicans) {
      if (pe.state !== 'fly') continue;
      const reach = Math.max(THROWABLES[h.kind].lure?.pelican?.(pe.d) ?? 0, PHYSICS.pelicanRadius(pe.d));
      if (Math.hypot(pe.x - h.x, pe.d - h.d, pe.h - h.h) > reach) continue;
      hitPelican(w, pe, h.kind);
      caught = true;
      break;
    }
    if (caught) {
      w.projectiles.splice(i, 1);
      continue;
    }
    if (h.h <= 0) {
      land(w, h.x, h.d, h.kind);
      w.projectiles.splice(i, 1);
    }
  }

  // effects age out
  w.splashes = w.splashes.filter((s) => w.t - s.t0 < s.life);
  w.pops = w.pops.filter((s) => w.t - s.t0 < 1.2);

  if (Number.isFinite(w.duration) && w.t >= w.duration) endRound(w, 'time');
}

/** things of this kind that are in the air */
export function inFlight(w: World, kind: ThrowKind = w.item): number {
  return w.projectiles.reduce((n, p) => n + (p.kind === kind ? 1 : 0), 0);
}

export function canFire(w: World): boolean {
  if (w.over) return false;
  if (w.ammo[w.item] <= 0) return false;
  if (inFlight(w) >= THROWABLES[w.item].maxInFlight) return false;
  return w.t >= w.reloadUntil;
}

/** the next thing in the bucket that still has some left, after `from`; null if the bucket is empty */
export function nextWithAmmo(w: World, from: ThrowKind): ThrowKind | null {
  const i = BUCKET_KINDS.indexOf(from);
  for (let k = 1; k <= BUCKET_KINDS.length; k++) {
    const kind = BUCKET_KINDS[(i + k) % BUCKET_KINDS.length];
    if (w.ammo[kind] > 0) return kind;
  }
  return null;
}

/** pick something else out of the bucket; false if nothing changed */
export function setItem(w: World, kind: ThrowKind): boolean {
  if (w.over || w.item === kind || w.ammo[kind] <= 0) return false;
  w.item = kind;
  w.events.push({ type: 'item', kind });
  return true;
}

const clamp = (x: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, x));

/** launch speed for a power in [0, 1]: sqrt so that range is linear in power */
export function speedFor(power: number, kind: ThrowKind = 'rock'): number {
  return THROWABLES[kind].vMax * Math.sqrt(clamp(power, 0.02, 1));
}

/** seconds in the air for a shot at this power and elevation (degrees) */
export function flightTime(power: number, elevation: number, kind: ThrowKind = 'rock'): number {
  const v = speedFor(power, kind);
  const vh = v * Math.sin(elevation * DEG);
  return (vh + Math.sqrt(vh * vh + 2 * PHYSICS.g * PHYSICS.launchH)) / PHYSICS.g;
}

/** metres from the rail a shot lands, for this power and elevation (degrees) */
export function rangeFor(power: number, elevation: number, kind: ThrowKind = 'rock'): number {
  return speedFor(power, kind) * Math.cos(elevation * DEG) * flightTime(power, elevation, kind);
}

/** the power that lands a shot `range` metres out at this elevation, or null if it is out of reach */
export function powerFor(range: number, elevation: number, kind: ThrowKind = 'rock'): number | null {
  if (rangeFor(1, elevation, kind) < range) return null;
  let lo = 0.02;
  let hi = 1;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (rangeFor(mid, elevation, kind) < range) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * Throw the thing in hand: `azimuth` degrees right of straight ahead, `elevation` degrees above
 * the horizontal, `power` in [0, 1]. False if the hand isn't ready.
 */
export function launch(w: World, azimuth: number, elevation: number, power: number): boolean {
  if (!canFire(w)) return false;
  const kind = w.item;
  const phi = clamp(azimuth, -PHYSICS.azimuthMax, PHYSICS.azimuthMax) * DEG;
  const theta = clamp(elevation, PHYSICS.elevationMin, PHYSICS.elevationMax) * DEG;
  const v = speedFor(power, kind);
  const proj: Projectile = {
    kind,
    x: 0,
    d: 0,
    h: PHYSICS.launchH,
    vx: v * Math.cos(theta) * Math.sin(phi),
    vd: v * Math.cos(theta) * Math.cos(phi),
    vh: v * Math.sin(theta),
    firedAt: w.t,
  };
  if (THROWABLES[kind].homing) {
    // the nearest target to the boat: the face in the Feed round, else a coconut
    if (w.face && w.face.state === 'up') {
      proj.target = { kind: 'face' };
      proj.arriveAt = w.t + 1.1;
    } else {
      let best: Coconut | null = null;
      for (const c of w.coconuts) if (c.state === 'afloat' && (!best || Math.hypot(c.x, c.d) < Math.hypot(best.x, best.d))) best = c;
      if (best) {
        proj.target = { kind: 'coconut', id: best.id };
        proj.arriveAt = w.t + Math.min(1.8, 0.6 + Math.hypot(best.x, best.d) / 30);
      }
    }
  }
  w.projectiles.push(proj);
  w.reloadUntil = w.t + THROWABLES[kind].reload;
  w.shots += 1;
  w.ammo[kind] -= 1;
  w.events.push({ type: 'fire', kind });
  if (w.ammo[kind] <= 0) {
    const next = nextWithAmmo(w, kind);
    w.events.push({ type: 'empty', kind, next });
    if (next) {
      w.item = next;
      w.events.push({ type: 'item', kind: next });
    }
  }
  return true;
}

/**
 * The shot that would land on a moving target right now, leading its drift (for tests and a
 * possible assist mode). Null if it is out of reach.
 */
export function solveShot(x: number, d: number, vx: number, vd: number, elevation = 45, kind: ThrowKind = 'rock'): { azimuth: number; elevation: number; power: number } | null {
  let tx = x;
  let td = d;
  let power: number | null = null;
  for (let i = 0; i < 3; i++) {
    power = powerFor(Math.hypot(tx, td), elevation, kind);
    if (power === null) return null;
    const tf = flightTime(power, elevation, kind);
    tx = x + vx * tf;
    td = d + vd * tf;
  }
  return { azimuth: Math.atan2(tx, td) / DEG, elevation, power: power! };
}

/** take the pending events (the view drains them each frame) */
export function drain(w: World): GameEvent[] {
  const e = w.events;
  w.events = [];
  return e;
}
